import { useEffect, useRef } from 'react';

const COLOURS = ['#f4d35e', '#e2574c', '#4ea87a', '#5b9dd9', '#ffffff', '#c9ab3f'];
const PARTICLE_COUNT = 150;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  colour: string;
  spin: number;
  angle: number;
  sway: number;
}

/**
 * Confetti, drawn on a canvas rather than as DOM nodes — 150 elements
 * animating at once would cost far more in layout than a single canvas, and
 * this keeps the bundle dependency-free.
 */
function useConfetti(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Anyone who has asked for less motion gets the message without the storm.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.scale(dpr, dpr);

    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * width,
      // Staggered above the fold so they arrive as a shower, not a wall.
      y: -Math.random() * height * 0.9 - 20,
      vx: (Math.random() - 0.5) * 1.4,
      vy: 2 + Math.random() * 3.2,
      w: 6 + Math.random() * 6,
      h: 9 + Math.random() * 7,
      colour: COLOURS[Math.floor(Math.random() * COLOURS.length)] as string,
      spin: (Math.random() - 0.5) * 0.22,
      angle: Math.random() * Math.PI * 2,
      sway: Math.random() * Math.PI * 2,
    }));

    let raf = 0;
    let start = 0;

    const frame = (now: number) => {
      if (!start) start = now;
      const elapsed = now - start;

      ctx.clearRect(0, 0, width, height);
      // Fade the whole thing out towards the end rather than cutting it off.
      ctx.globalAlpha = elapsed > 1800 ? Math.max(0, 1 - (elapsed - 1800) / 900) : 1;

      for (const p of particles) {
        p.sway += 0.05;
        p.x += p.vx + Math.sin(p.sway) * 0.7;
        p.y += p.vy;
        p.vy += 0.035;
        p.angle += p.spin;

        if (p.y > height + 20) {
          p.y = -20;
          p.x = Math.random() * width;
          p.vy = 2 + Math.random() * 3.2;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.colour;
        // Squashing on the vertical axis reads as a fluttering paper strip.
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.sway)));
        ctx.restore();
      }

      if (elapsed < 2700) raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef]);
}

export interface CelebrationProps {
  winnerName: string;
  isYou: boolean;
}

/** Shown for a beat between the winning card landing and the scoreboard. */
export function Celebration({ winnerName, isYou }: CelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useConfetti(canvasRef);

  return (
    <div className="celebration" role="status" aria-live="polite">
      <canvas ref={canvasRef} className="celebration__canvas" aria-hidden="true" />
      <div className="celebration__banner">
        <p className="celebration__line">
          {isYou ? 'You played your last card!' : `${winnerName} played their last card!`}
        </p>
        <p className="celebration__sub">{isYou ? 'You win.' : 'Round over.'}</p>
      </div>
    </div>
  );
}
